import { FC, useEffect, useMemo } from "react";
import { FieldError, useController, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useInputDisabled } from "capitalrx-components-2.0/lib/forms/InputGroup/useInputDisabled";
import { useInputReadonly } from "capitalrx-components-2.0/lib/forms/InputGroup/useInputReadonly";
import { SubmitGroup } from "capitalrx-components-2.0/lib/forms/SubmitGroup";
import { FieldProps } from "capitalrx-components-2.0/lib/forms/Textbox/Field";
import { DisabledLengthRule, DisabledRequiredRule } from "capitalrx-components-2.0/lib/forms/validation/rules";
import { ValidityGroup } from "capitalrx-components-2.0/lib/forms/ValidityGroup";

import { ERRORS, length as lengthRule, PREFIXED_ERRORS, required as requiredRule, RuleError } from "@/App/utils/validation";

import { Input } from "./Input";

interface AppFieldProps extends FieldProps {
  readonly useDigitsCounter?: boolean;
}

export const Field: FC<AppFieldProps> = (props) => {
  const {
    name,
    required = false,
    minLength,
    maxLength,
    defaultValue = null,
    validator,
    useDigitsCounter,
    ...restProps
  } = props;

  const { t: translate } = useTranslation();

  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);

  const rules = useMemo(() => ({
    ...(disabled || readOnly ? DisabledRequiredRule : requiredRule(required)),
    ...(disabled || readOnly ? DisabledLengthRule : lengthRule(minLength, maxLength)),
    validate: disabled || readOnly ? undefined : validator,
  }), [disabled, readOnly, required, minLength, maxLength, validator]);

  const { resetField } = useFormContext();

  useEffect(() => {
    if (disabled) {
      resetField(name);
    }
  }, [disabled]);

  const { field, fieldState, formState } = useController({
    name,
    defaultValue,
    rules,
  });

  const { ref, value, onChange, onBlur } = field;
  const { invalid, error } = fieldState;
  const { isSubmitting } = formState;

  const getTranslatedErrorMessage = (error: FieldError | undefined) => {
    if (!error || !error?.message) {
      return undefined;
    }

    if (!ERRORS.includes(error.message as RuleError)) {
      return error.message;
    }

    let errorPrefix = "";

    if (PREFIXED_ERRORS.includes(error.message as RuleError)) {
      errorPrefix = useDigitsCounter ? "digits_" : "characters_";
    }

    return translate(`FORM_FIELD_VALIDATION_ERRORS.${errorPrefix}${error.message}`, { min: minLength, max: maxLength, count: minLength });
  };

  return (
    <SubmitGroup submitting={isSubmitting}>
      <ValidityGroup invalid={invalid} error={getTranslatedErrorMessage(error)}>
        <Input
          {...restProps}
          ref={ref}
          value={value}
          type="text"
          required={required}
          maxLength={maxLength}
          defaultValue={defaultValue}
          onChange={onChange}
          onFocusExit={onBlur}
          useDigitsCounter={useDigitsCounter}
        />
      </ValidityGroup>
    </SubmitGroup>
  );
};
